import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Check } from "lucide-react";
import { createPledge } from "@/lib/api";
import { toast } from "sonner";

const PledgeModal = ({ open, onOpenChange, campaign, onSuccess }) => {
  const [selectedReward, setSelectedReward] = useState(campaign?.rewards?.[0]?.id || null);
  const [amount, setAmount] = useState(campaign?.rewards?.[0]?.price || 25);
  const [busy, setBusy] = useState(false);

  const pickReward = (r) => {
    setSelectedReward(r.id);
    setAmount(r.price);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const pledge = await createPledge(campaign.id, {
        reward_id: selectedReward,
        amount: Math.max(1, parseInt(amount, 10) || 0),
      });
      toast.success("Pledge recorded — thank you!", {
        description: "Payments (Razorpay) will be enabled in the next release."
      });
      onSuccess?.(pledge);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record pledge");
    } finally {
      setBusy(false);
    }
  };

  if (!campaign) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] rounded-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="pledge-modal">
        <div className="p-8">
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-2xl tracking-tight">Back this project</DialogTitle>
            <DialogDescription className="text-navy-500 truncate">{campaign.title}</DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-6" data-testid="pledge-form">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Choose a reward</Label>
              {(campaign.rewards || []).map((r) => (
                <button
                  type="button" key={r.id}
                  onClick={() => pickReward(r)}
                  data-testid={`pledge-reward-${r.id}`}
                  className={`w-full text-left rounded-2xl border p-4 transition-all ${selectedReward === r.id ? "border-saffron-500 bg-saffron-50/40 ring-2 ring-saffron-500/20" : "border-warm-200 hover:border-saffron-200"}`}
                >
                  <div className="flex justify-between items-baseline">
                    <div>
                      <div className="font-medium text-navy-800">{r.title}</div>
                      <div className="text-xs text-navy-500 mt-0.5">{r.description}</div>
                    </div>
                    <div className="font-display text-lg font-semibold text-navy-800">${r.price}</div>
                  </div>
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setSelectedReward(null); }}
                className={`w-full text-left rounded-2xl border p-4 transition-all ${selectedReward === null ? "border-saffron-500 bg-saffron-50/40 ring-2 ring-saffron-500/20" : "border-warm-200 hover:border-saffron-200"}`}
                data-testid="pledge-reward-none"
              >
                <div className="flex justify-between items-baseline">
                  <div>
                    <div className="font-medium text-navy-800">No reward — just support</div>
                    <div className="text-xs text-navy-500 mt-0.5">Contribute any amount. Every dollar counts.</div>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-6">
              <Label htmlFor="pledge-amount" className="text-sm font-medium">Your pledge (USD)</Label>
              <div className="relative mt-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-navy-500 font-medium">$</span>
                <Input
                  id="pledge-amount" data-testid="pledge-amount-input"
                  type="number" min="1" required
                  value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="pl-8 h-12 rounded-2xl border-warm-200"
                />
              </div>
              <p className="mt-2 text-xs text-navy-500">Recorded now. Payment integration (Razorpay) coming soon.</p>
            </div>

            <Button
              type="submit" disabled={busy} data-testid="pledge-submit-btn"
              className="mt-6 w-full h-12 rounded-2xl bg-saffron-600 hover:bg-saffron-700 hover:scale-[1.02] active:scale-95 transition-all"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Check className="h-4 w-4 mr-2" />Confirm pledge</>)}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PledgeModal;
